/**
 * Generate an RFC-4122 version-4 UUID.
 *
 * Unlike getGuid(), this sets the version (4) and variant (10xx) nibbles required by RFC-4122, so the
 * result passes strict UUID validators. Use it whenever an id is handed to a service that validates
 * the UUID shape -- e.g. the headless OfficeAgent conversationId: generateOfficeAgentUrlInternal
 * rejects a non-UUID id and silently falls back to a shared, sessionKey-derived container, which
 * breaks per-conversation container isolation and telemetry correlation. Prefers the platform
 * crypto.randomUUID (polyfilled in owa-polyfills) and falls back to a getRandomValues/Math.random
 * builder for environments without it.
 */
export function getUuidV4(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }

    const bytes = new Uint8Array(16);
    if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
        crypto.getRandomValues(bytes);
    } else {
        for (let i = 0; i < 16; i++) {
            bytes[i] = Math.floor(Math.random() * 256);
        }
    }
    // Force the version (4) and variant (10xx) bits required by RFC-4122.
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;

    const hex = (byte: number) => (byte + 0x100).toString(16).substring(1);
    const b = Array.from(bytes, hex);
    return `${b[0]}${b[1]}${b[2]}${b[3]}-${b[4]}${b[5]}-${b[6]}${b[7]}-${b[8]}${b[9]}-${b[10]}${b[11]}${b[12]}${b[13]}${b[14]}${b[15]}`;
}
